import React, {useState, useEffect} from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Stack,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem
} from '@mui/material';
import {UploadFile, Delete} from '@mui/icons-material';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';

export default function FeedbackPopup({open, onClose}) {
    const [feedback, setFeedback] = useState('');
    const [shortDescription, setShortDescription] = useState('');
    const [type, setType] = useState('bug');
    const [files, setFiles] = useState([]);
    const [visibleFiles, setVisibleFiles] = useState([]);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (open) {
            setFeedback('');
            setShortDescription('');
            setType('bug');
            setFiles([]);
            setVisibleFiles([]);
            setSubmitted(false);
        }
    }, [open]);

    const MAX_VISIBLE_FILES = 3;

    const handleFileChange = (event) => {
        const newFiles = [...files, ...event.target.files];
        setFiles(newFiles);
        setVisibleFiles(newFiles.slice(0, MAX_VISIBLE_FILES));
    };

    const handleFileDelete = (index) => {
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);

        const updatedVisibleFiles = updatedFiles.slice(0, MAX_VISIBLE_FILES);
        setVisibleFiles(updatedVisibleFiles);
    };

    const handleSubmit = () => {
        if (type && (shortDescription.trim() || feedback.trim() || files.length > 0)) {
            console.log('Feedback submitted:', {type, shortDescription, feedback});
            console.log('Files submitted:', files);
            setSubmitted(true);
            setShortDescription('');
            setFeedback('');
            setType('');
            setFiles([]);
            setVisibleFiles([]);
            setTimeout(() => {
                onClose(); // Close the dialog after 100ms
            }, 100);
        }
    };

    const hiddenFilesCount = files.length - visibleFiles.length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Feedback</DialogTitle>
            <DialogContent>
                <Box sx={{p: 4, maxWidth: 600, mx: 'auto'}}>
                    <Typography variant="body1" gutterBottom>
                        We value your feedback! Please let us know your thoughts, suggestions, or any issues you faced.
                        You can
                        also upload screenshots or files to help us understand better.
                    </Typography>
                    <Stack spacing={2} mt={2}>
                        <TextField
                            id="feedback-type"
                            select
                            label="Feedback Type"
                            defaultValue="bug"
                            variant="standard"
                            onChange={(e) => setType(e.target.value)}
                        >
                            <MenuItem value="feature">Feature Recommendation</MenuItem>
                            <MenuItem value="bug">Bug</MenuItem>
                            <MenuItem value="issue">Issue</MenuItem>
                            <MenuItem value="ui">UI Improvement</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </TextField>

                        <TextField
                            label="Short Description"
                            value={shortDescription}
                            onChange={(e) => setShortDescription(e.target.value)}
                            fullWidth
                            variant="standard"
                            placeholder="Provide a brief summary of your feedback"
                        />

                        <TextField
                            label="Detailed Description"
                            multiline
                            rows={4}
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            fullWidth
                            variant="standard"
                            placeholder="Provide a detailed explanation of your feedback"
                        />
                        <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                            <Button
                                variant="outlined"
                                size="small"
                                component="label"
                                startIcon={<UploadFile/>}
                                sx={{
                                    fontSize: '0.75rem',
                                    padding: '2px 6px',
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                }}
                            >
                                Upload Files
                                <input type="file" hidden multiple onChange={handleFileChange}/>
                            </Button>
                        </Box>

                        {visibleFiles.length > 0 && (
                            <List>
                                {visibleFiles.map((file, index) => (
                                    <ListItem
                                        key={index}
                                        secondaryAction={
                                            <IconButton edge="end" onClick={() => handleFileDelete(index)}>
                                                <Delete/>
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={file.name}/>
                                    </ListItem>
                                ))}
                                {hiddenFilesCount > 0 && (
                                    <ListItem>
                                        <ListItemText primary={`+${hiddenFilesCount} more`}/>
                                    </ListItem>
                                )}
                            </List>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={!type || (!shortDescription.trim() && !feedback.trim() && files.length === 0)}
                            sx={{
                                borderRadius: 2,
                                paddingX: 3,
                                paddingY: 1,
                                '&.Mui-disabled': {
                                    color: (theme) => theme.palette.mode === 'light' ? 'rgb(131,128,128)' : 'rgba(0,0,0,0.27)', // Adjust for light and dark modes
                                    backgroundColor: (theme) => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)', // Optional: Adjust background
                                },
                            }}
                        >
                            Submit
                        </Button>
                        {submitted && (
                            <Typography variant="body2" color="success.main">
                                Thank you for your feedback!
                            </Typography>
                        )}
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    );
}